/**
 * VistoeraTASK-00562 — where to land after an account switch.
 *
 * Before: every switch hard-navigated to `/workspace`, which redirects to the
 * Home Canvas, so a user on 图片/音频/文本资产 or 团队管理 was thrown onto Canvas.
 *
 * Now: the pre-switch location is mapped onto the NEW account's context.
 * Rules (pure, no I/O; display-side only — the backend still authorises every
 * request, and route guards such as `RequireTeamManager` / `RequireFeature`
 * remain the second line):
 *  - only same-app `/workspace/...` and `/platform/...` paths are ever kept;
 *  - the workspace segment is re-mapped to the new account's workspace unless
 *    the new account is a member of the same workspace;
 *  - resource ids (project / site / chain / asset / ...) are NEVER carried
 *    across accounts — the route collapses to its list page;
 *  - query string and hash are dropped (may hold old-account ids/filters);
 *  - Team Management is kept only when the new account's membership says it
 *    may access it, else the safe workspace Overview;
 *  - anything unknown falls back to the safe workspace Overview.
 */
import { canAccessTeamManagement, TEAM_TABS } from '../workspace/team/teamPermissions'

export interface SwitchTargetWorkspace {
  workspace_id: string
  role?: string | null
  team_mode_enabled?: boolean | null
}

export interface SwitchTargetMe {
  is_platform_admin: boolean
  workspaces: SwitchTargetWorkspace[]
}

const WORKSPACE_PAGES = new Set([
  'ai-chat',
  'overview',
  'home',
  'storage',
  'website',
  'images',
  'audio-assets',
  'text-assets',
  'video-generation',
  'content-production',
  'competitive-content-analysis',
  'content-publishing',
  'local-publishers',
  'domains',
  'publish',
])

const ACCOUNT_SUB_PAGES = new Set(['profile', 'plan-usage', 'settings', 'help'])
const VIDEO_SUB_PAGES = new Set(['continuation', 'assets', 'projects', 'classic'])
const IMAGES_SUB_PAGES = new Set(['process', 'flat-optimization'])
const PLATFORM_PAGES = new Set([
  'marketing-analytics',
  'overview',
  'tenants',
  'tenant-plans',
  'plan-auth-matrix',
  'sites',
  'domains',
  'workers',
  'engine-profiles',
  'agent-runtimes',
  'skills',
  'providers',
  'models',
  'model-capabilities',
  'credential-providers',
  'provider-credentials',
  'storage',
  'rate-rules',
  'plan-credits',
  'ai-billing-rules',
  'ai-cost-pricing',
  'system-settings',
  'security',
  'external-services',
])
const PLATFORM_AI_CHAT_SUB = new Set(['tasks', 'conversations'])

function segmentsOf(pathname: string): string[] {
  const clean = (pathname || '').split(/[?#]/)[0]
  return clean.split('/').filter(Boolean)
}

function defaultRoute(me: SwitchTargetMe): string {
  return me.is_platform_admin && me.workspaces.length === 0 ? '/platform' : '/workspace'
}

/** Pure: workspace-relative segments → safe, id-free relative path. */
function resolveWorkspaceRelative(rel: string[], workspace: SwitchTargetWorkspace): string {
  if (rel.length === 0) return 'overview'
  const [first, second] = rel

  // VistoeraTASK-00581/00582 — leaving a Canvas stays on Canvas via the
  // fullscreen `/home` entry (last-opened → Home Canvas). Never land on a
  // Workbench intermediate (`canvas-resume` used to flash the sider). Never
  // carry A's project id. The content-projects LIST stays the list when the
  // user was already on that workbench page.
  if (first === 'content-production') {
    if (rel[2] === 'canvas') return 'home'
    return 'content-production'
  }
  if (first === 'home' || first === 'canvas-resume') return 'home'
  if (first === 'team') {
    if (!canAccessTeamManagement({ team_mode_enabled: workspace.team_mode_enabled, my_role: workspace.role })) {
      return 'overview'
    }
    const tab = TEAM_TABS.find((t) => t.path !== '' && t.path === second)
    if (!tab) return 'team'
    if ('ownerOnly' in tab && tab.ownerOnly && workspace.role !== 'owner') return 'team'
    return `team/${tab.path}`
  }
  if (first === 'account') return ACCOUNT_SUB_PAGES.has(second ?? '') ? `account/${second}` : 'overview'
  if (first === 'virtual-ip') return second === 'assets' ? 'virtual-ip/assets' : 'virtual-ip/projects'
  if (first === 'images') return IMAGES_SUB_PAGES.has(second ?? '') ? `images/${second}` : 'images'
  if (first === 'video-generation') {
    return VIDEO_SUB_PAGES.has(second ?? '') ? `video-generation/${second}` : 'video-generation'
  }
  if (WORKSPACE_PAGES.has(first)) return first
  return 'overview'
}

function resolvePlatformRoute(segments: string[]): string {
  const sub = segments[1]
  if (!sub) return '/platform'
  if (sub === 'ai-chat') {
    return PLATFORM_AI_CHAT_SUB.has(segments[2] ?? '') ? `/platform/ai-chat/${segments[2]}` : '/platform'
  }
  return PLATFORM_PAGES.has(sub) ? `/platform/${sub}` : '/platform'
}

/**
 * Maps the pre-switch pathname onto the switched-to account.
 * Returns an in-app absolute path, never an external URL.
 */
export function resolvePostSwitchRoute(pathname: string, target: SwitchTargetMe): string {
  const segments = segmentsOf(pathname)

  if (segments[0] === 'platform') {
    return target.is_platform_admin ? resolvePlatformRoute(segments) : defaultRoute(target)
  }

  if (segments[0] === 'workspace' && segments.length >= 2) {
    const oldWorkspaceId = segments[1]
    const workspace =
      target.workspaces.find((w) => w.workspace_id === oldWorkspaceId) ?? target.workspaces[0] ?? null
    if (!workspace) return defaultRoute(target)
    const relative = resolveWorkspaceRelative(segments.slice(2), workspace)
    return `/workspace/${workspace.workspace_id}/${relative}`
  }

  return defaultRoute(target)
}
